import jobsData from '../data/jobs.json';

const CUSTOM_INDUSTRIES_KEY = 'admin_custom_industries';

export interface Industry {
  id: string;
  name: string;
  icon: string;
  metaTitle: string;
  metaDescription: string;
  description: string;
  longDescription: string;
  expertise: string[];
  benefits: string[];
  challenges: string[];
  solutions: {
    name: string;
    description: string;
    benefits: string[];
    technologies: string[];
  }[];
  stats: {
    clients: number;
    projectsCompleted: number;
    successRate: string;
    roi: string;
  };
  testimonials: {
    author: string;
    position: string;
    content: string;
  }[];
  faq: {
    question: string;
    answer: string;
  }[];
}

class IndustryService {
  private industries: Industry[];
  private subscribers: ((industries: Industry[]) => void)[] = [];

  constructor() {
    this.industries = this.loadIndustries();
    
    window.addEventListener('storage', (e) => {
      if (e.key === CUSTOM_INDUSTRIES_KEY) {
        this.industries = this.loadIndustries();
        this.notifySubscribers();
      }
    });
  }

  private loadIndustries(): Industry[] {
    try {
      const defaultIndustries = jobsData.industries;
      const storedCustomIndustries = localStorage.getItem(CUSTOM_INDUSTRIES_KEY);
      const customIndustries = storedCustomIndustries ? JSON.parse(storedCustomIndustries) : [];
      
      const processedIndustries = new Map();
      
      customIndustries.forEach((customIndustry: Industry) => {
        processedIndustries.set(customIndustry.id, this.normalizeIndustry(customIndustry));
      });
      
      defaultIndustries.forEach(defaultIndustry => {
        if (!processedIndustries.has(defaultIndustry.id)) {
          processedIndustries.set(defaultIndustry.id, this.normalizeIndustry(defaultIndustry));
        }
      });
      
      return Array.from(processedIndustries.values()).sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Error loading industries:', error);
      return jobsData.industries;
    }
  }

  private saveIndustries() {
    try {
      const defaultIndustriesMap = new Map(
        jobsData.industries.map(i => [i.id, JSON.stringify(this.normalizeIndustry(i))])
      );
      
      const customIndustries = this.industries.filter(industry => {
        const normalizedIndustry = this.normalizeIndustry(industry);
        const defaultIndustryStr = defaultIndustriesMap.get(industry.id);
        return !defaultIndustryStr || defaultIndustryStr !== JSON.stringify(normalizedIndustry);
      });

      localStorage.setItem(CUSTOM_INDUSTRIES_KEY, JSON.stringify(customIndustries));
      
      this.notifySubscribers();
      
      window.dispatchEvent(new StorageEvent('storage', {
        key: CUSTOM_INDUSTRIES_KEY,
        newValue: JSON.stringify(customIndustries)
      }));
    } catch (error) {
      console.error('Error saving industries:', error);
    }
  }

  private normalizeIndustry(industry: Industry): Industry {
    return {
      id: industry.id,
      name: industry.name || '',
      icon: industry.icon || '🏢',
      metaTitle: industry.metaTitle || '',
      metaDescription: industry.metaDescription || '',
      description: industry.description || '',
      longDescription: industry.longDescription || '',
      expertise: Array.isArray(industry.expertise) ? [...industry.expertise] : [],
      benefits: Array.isArray(industry.benefits) ? [...industry.benefits] : [],
      challenges: Array.isArray(industry.challenges) ? [...industry.challenges] : [],
      solutions: Array.isArray(industry.solutions) ? industry.solutions.map(solution => ({
        name: solution.name || '',
        description: solution.description || '',
        benefits: Array.isArray(solution.benefits) ? [...solution.benefits] : [],
        technologies: Array.isArray(solution.technologies) ? [...solution.technologies] : []
      })) : [],
      stats: {
        clients: Number(industry.stats?.clients) || 0,
        projectsCompleted: Number(industry.stats?.projectsCompleted) || 0,
        successRate: industry.stats?.successRate || '0%',
        roi: industry.stats?.roi || '0%'
      },
      testimonials: Array.isArray(industry.testimonials) ? industry.testimonials.map(testimonial => ({
        author: testimonial.author || '',
        position: testimonial.position || '',
        content: testimonial.content || ''
      })) : [],
      faq: Array.isArray(industry.faq) ? industry.faq.map(item => ({
        question: item.question || '',
        answer: item.answer || ''
      })) : []
    };
  }

  private notifySubscribers() {
    this.subscribers.forEach(callback => callback(this.industries));
  }

  public subscribe(callback: (industries: Industry[]) => void) {
    this.subscribers.push(callback);
    callback(this.industries);
    return () => {
      this.subscribers = this.subscribers.filter(cb => cb !== callback);
    };
  }

  public getIndustries(): Industry[] {
    return [...this.industries];
  }

  public updateIndustry(industry: Industry) {
    const index = this.industries.findIndex(i => i.id === industry.id);
    const normalizedIndustry = this.normalizeIndustry(industry);
    
    if (index !== -1) {
      this.industries[index] = normalizedIndustry;
    } else {
      this.industries.push(normalizedIndustry);
    }
    
    this.saveIndustries();
  }

  public deleteIndustry(id: string) {
    this.industries = this.industries.filter(i => i.id !== id);
    this.saveIndustries();
  }
}

export const industryService = new IndustryService();